
/// <amd-module name="Build/Run/SignalRService" />
import { IRunArtifactsService } from "Build/Run/RunArtifactsService.types";
import { IRunDetailsService } from "Build/Run/RunDetailsService.types";
import { IRunLogsService } from "Build/Run/RunLogsService.types";

import { getBuildProxyToRegister, HubNames, IBuildDetailHubClient, IBuildDetailHubServer } from "Build/Common/Library/SignalRHubProxies";
import { getDTProxyToRegister, HubNames as DTHubNames, ITaskAgentPoolHubClient, ITaskAgentPoolHubServer } from "DistributedTask/Common/SignalR/SignalRHubProxies";

import { IHubHostInformation, ISingleConnectionSignalRHubOptions, SingleConnectionSignalRHub } from "VSS/Features/SignalR/Hub";
import { ISignalRHubProxy } from "VSS/Features/SignalR/HubProxies.types";

import { ConsoleLogEvent, BuildStatus } from "Build/Client/Contracts/Build";

import { ITfsPageService } from "Tfs/Platform/Page";

import { TaskAgentJobRequest } from "DistributedTask/Client/Contracts/TaskAgent";

import { ISignalROptions } from "Build/Run/SignalRService.types";
import { ITestHubSignalRSync, ITestSignalRHubService } from "Test/TestReportingSignalR/TestHub";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssHistoryService } from "VSS/Platform/History";
import { IVssPageService } from "VSS/Platform/Page";
import { RunDetailsData } from "Build/Client/Contracts/DataProvider";

const c_userInactivityTimeoutMs = 1 * 60 * 60 * 1000; // 1 hour

export interface ISignalRService extends IVssService {
    watch: (buildId: number) => void;
    endWatch: (buildId: number) => void;
    watchPool: (poolId: number) => void;
    endWatchPool: (poolId: number) => void;
    watchTestHub: (buildId: number, runDetailsData: RunDetailsData) => void;
    endWatchTestHub: (buildId: number) => void;
}

export class SignalRService extends VssService implements ISignalRService {
    private _buildHub: BuildHub;
    private _poolHub: PoolHub;
    private _testHub: ITestSignalRHubService;
    private _testHubInitialize: Promise<void>;
    private _testHubStarted: Promise<void>;
    private _watchingPoolId: number | undefined;

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);
        this._initialize();
    }

    public _serviceEnd(pageContext: IVssPageContext): void {
        super._serviceEnd(pageContext);
        if (this._buildHub) {
            this._buildHub.dispose();
        }
        if (this._poolHub) {
            this._poolHub.dispose();
        }
        if (this._testHub && this._testHubStarted) {
            this._testHubStarted.then(()=>{
                this._testHub.dispose();
            });
        }
    }

    public watch(buildId: number) {
        if (this._buildHub) {
            this._buildHub.start().then(() => {
                this._buildHub.watch(buildId);
            });
        }
    }

    public watchTestHub(buildId: number, runDetailsData: RunDetailsData) {
        // testHub will be sending signals only during the build/pipeline inprogress. 
        // Not calling testHub watchBuild if the status is not InProgress or NotStarted.
        if(runDetailsData.status !== BuildStatus.InProgress && runDetailsData.status !== BuildStatus.NotStarted) {
            return;
        }

        if (this._testHub) {
            this._testHubInitialize.then(() => {
                this._testHubStarted = this._testHub.watchBuild(buildId);
            });
        }
    }

    public endWatchTestHub(buildId: number) {
        if (this._testHub && this._testHubStarted) {
            this._testHubStarted.then(() => {
                this._testHub.unwatchBuild(buildId).then(() => {
                    // Disposing the connection itself, as there will be unnecessary ping calls to the AT.
                    this._testHub.dispose();
                })
            });
        }
    }

    public watchPool(poolId: number) {
        if (this._watchingPoolId !== poolId) {
            if (this._poolHub) {
                if (this._watchingPoolId) {
                    this.endWatchPool(this._watchingPoolId);
                }

                this._watchingPoolId = poolId;
                this._poolHub.start().then(() => {
                    this._poolHub.watch(poolId);
                });
            }
        }
    }

    public endWatch(buildId: number) {
        if (this._buildHub) {
            this._buildHub.start().then(() => {
                this._buildHub.endWatch(buildId);
            });
        }
    }

    public endWatchPool(poolId: number) {
        if (this._poolHub) {
            this._poolHub.start().then(() => {
                delete this._watchingPoolId;
                this._poolHub.endWatch(poolId);
            });
        }
    }

    private _initialize() {
        const vssPageService = this.pageContext.getService<IVssPageService>("IVssPageService");
        const vssPageData = vssPageService.getData();
        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        const historyService = this.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const notificationSync = new TestHubSignalRSync(this.pageContext);
        if (vssPageData && tfsPageData && tfsPageData.project) {
            const hostInformation: IHubHostInformation = {
                hostId: vssPageData.hostId,
                hostPath: vssPageData.hostPath,
                isHosted: vssPageData.isHosted,
                projectId: tfsPageData.project.id
            };
            const options: ISignalROptions = {
                hostInformation: hostInformation,
                pageContext: this.pageContext,
                historyService: historyService
            };

            this._buildHub = new BuildHub(options);
            this._poolHub = new PoolHub(options);
            this._testHub = this.pageContext.getService<ITestSignalRHubService>("ITestSignalRHub");
            this._testHubInitialize = this._testHub.initialize(this.pageContext, tfsPageData.project.id, notificationSync);
        }
    }
}

Services.add("ISignalRService", { serviceFactory: SignalRService });

/**
 * Used by both logs and summary pages
 */
export class BuildHub extends SingleConnectionSignalRHub<IBuildDetailHubClient, IBuildDetailHubServer> {
    private _options: ISignalROptions;

    constructor(options: ISignalROptions) {
        super({
            name: HubNames.buildDetailHub,
            hostInformation: options.hostInformation,
            userInactivityTimeoutMs: c_userInactivityTimeoutMs,
            proxyToRegister: getBuildProxyToRegister,
            pageContext: options.pageContext
        } as ISingleConnectionSignalRHubOptions<IBuildDetailHubClient, IBuildDetailHubServer>);

        this._options = options;
        this.initializeProxyClient(HubNames.buildDetailHub, this._initializeClient);
    }

    public start() {
        return super.start();
    }

    public watch(buildId: number) {
        const hub = this._hub;
        if (hub) {
            hub.server.watchBuild(this._options.hostInformation.projectId as string, buildId);
        }
    }

    public endWatch(buildId: number) {
        const hub = this._hub;
        if (hub) {
            hub.server.stopWatchingBuild(this._options.hostInformation.projectId as string, buildId);
        }
    }

    public dispose() {
        super.dispose();
    }

    private get _hub() {
        return this.getHub(HubNames.buildDetailHub);
    }

    private _initializeClient = (proxy: ISignalRHubProxy<IBuildDetailHubClient, IBuildDetailHubServer>) => {
        proxy.client.buildUpdated2 = (definitionId: number, buildId: number) => {
            const view = this._getView();
            if (view === Views.Summary) {
                this._detailsService.refreshData();
            }
        };
        proxy.client.timelineRecordsUpdated = (buildId: number, planId: string, timelineId: string, changeId: number) => {
            const view = this._getView();
            if (view === Views.Summary) {
                this._detailsService.refreshData();
            }
            if (view === Views.Logs) {
                this._logsService.onLiveTimelineUpdate(timelineId, changeId);
            }
        }
        proxy.client.logConsoleLines = (event: ConsoleLogEvent) => {
            const view = this._getView();
            if (view === Views.Logs) {
                this._logsService.onLiveContent({
                    content: event.lines.join("\n"),
                    jobId: event.timelineRecordId,
                    taskId: event.stepRecordId,
                    timelineId: event.timelineId
                });
            }
        };
        proxy.client.buildArtifactAdded = (buildId: number, artifactName: string): void => {
            const view = this._getView();
            if (view === Views.Artifacts) {
                this._artifactsService.refreshData();
            }
            if (view === Views.Summary) {
                this._detailsService.refreshArtifactsData();
            }
        }
    }

    private _getView(): Views {
        const state = this._options.historyService.getState();
        const viewName = ((state && state["view"]) || "").toLowerCase();
        switch (viewName) {
            case "logs":
                return Views.Logs;
            case "artifacts":
                return Views.Artifacts;
            default:
                return Views.Summary;
        }
    }

    private get _logsService() {
        return this._options.pageContext.getService<IRunLogsService>("IRunLogsService");
    }

    private get _detailsService() {
        return this._options.pageContext.getService<IRunDetailsService>("IRunDetailsService");
    }

    private get _artifactsService() {
        return this._options.pageContext.getService<IRunArtifactsService>("pipelines-run-published-artifacts-tab-provider-service");
    }
}

/**
 * Used only by logs view
 */
export class PoolHub extends SingleConnectionSignalRHub<ITaskAgentPoolHubClient, ITaskAgentPoolHubServer> {
    private _options: ISignalROptions;

    constructor(options: ISignalROptions) {
        super({
            name: DTHubNames.taskAgentPoolHub,
            hostInformation: options.hostInformation,
            userInactivityTimeoutMs: c_userInactivityTimeoutMs,
            proxyToRegister: getDTProxyToRegister,
            pageContext: options.pageContext
        } as ISingleConnectionSignalRHubOptions<ITaskAgentPoolHubClient, ITaskAgentPoolHubServer>);

        this._options = options;
        this.initializeProxyClient(DTHubNames.taskAgentPoolHub, this._initializeClient);
    }

    public start() {
        return super.start();
    }

    public watch(poolId: number) {
        const hub = this._hub;
        if (hub) {
            hub.server.subscribe(poolId);
        }
    }

    public endWatch(poolId: number) {
        const hub = this._hub;
        if (hub) {
            hub.server.unsubscribe(poolId);
        }
    }

    public dispose() {
        super.dispose();
    }

    private get _hub() {
        return this.getHub(DTHubNames.taskAgentPoolHub);
    }

    private _initializeClient = (proxy: ISignalRHubProxy<ITaskAgentPoolHubClient, ITaskAgentPoolHubServer>) => {
        proxy.client.notifyAgentConnected = (poolId: number, agentId: number) => {
            this._logsService.onLiveAgentStatusUpdate(poolId, agentId);
        };
        proxy.client.notifyAgentDisconnected = (poolId: number, agentId: number) => {
            this._logsService.onLiveAgentStatusUpdate(poolId, agentId);
        };
        proxy.client.notifyAgentRequestAssigned = (poolId: number, request: TaskAgentJobRequest) => {
            this._logsService.onLiveRequestUpdate(poolId, request);
        }
        proxy.client.notifyAgentRequestQueued = (poolId: number, request: TaskAgentJobRequest) => {
            this._logsService.onLiveRequestUpdate(poolId, request, true);
        }
        proxy.client.notifyAgentRequestStarted = (poolId: number, request: TaskAgentJobRequest) => {
            this._logsService.onLiveRequestUpdate(poolId, request);
        }
        proxy.client.notifyAgentRequestCompleted = (poolId: number, request: TaskAgentJobRequest) => {
            this._logsService.onLiveRequestUpdate(poolId, request);
        }
    }

    private get _logsService() {
        return this._options.pageContext.getService<IRunLogsService>("IRunLogsService");
    }
}

class TestHubSignalRSync implements ITestHubSignalRSync {
    private _runDetailsService: IRunDetailsService;

    constructor(pageContext: IVssPageContext) {
        this._runDetailsService = pageContext.getService<IRunDetailsService>("IRunDetailsService");
    }

    public testRunStatsChangedForBuild(buildId: number): void {
        this._runDetailsService.updateTestPassRate();
        this._runDetailsService.updateCodeCoverageSummary();
    }

    public testRunStatsChangedForRelease(releaseId: number, releaseEnvironmentId: number): void {
     // Do Nothing;
    }
}

enum Views {
    Summary,
    Logs,
    Artifacts
}